import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LessonQuizItem } from '../../types/course';
import { CoursePictures } from './CoursePictures';
import styles from './Course.module.css';
import { InlineText } from './InlineText';

interface Props {
  item: LessonQuizItem;
  number: number;
  answeredRight: boolean;
  onAnswer: (optionIndex: number) => Promise<void>;
  error?: string | null;
  onDiscuss?: () => void;
}

export function Quiz({ item, number, answeredRight, onAnswer, error, onDiscuss }: Props): JSX.Element {
  const { t } = useTranslation('course');
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const answered = picked !== null;
  const right = answered && picked === item.correctIndex;

  async function pick(i: number): Promise<void> {
    if (busy || answered) return;
    setBusy(true);
    try {
      await onAnswer(i);
      setPicked(i);
    } catch {
      /* the page shows the error; the pick stays open */
    } finally {
      setBusy(false);
    }
  }

  function optionClass(i: number): string {
    if (!answered) return styles.option;
    if (i === item.correctIndex) return `${styles.option} ${styles.optionRight}`;
    if (i === picked) return `${styles.option} ${styles.optionWrong}`;
    return styles.option;
  }

  return (
    <article className={styles.card} data-testid={`quiz-${item.id}`}>
      <h4 className={styles.h4}>{t('lesson.question', { n: number })}</h4>
      <p className={styles.text}><InlineText text={item.question} /></p>
      <CoursePictures images={[item.image, ...(item.extraImages ?? [])]} />
      <div className={styles.options}>
        {item.options.map((o, i) => (
          <button
            key={i}
            type="button"
            className={optionClass(i)}
            disabled={busy || answered}
            onClick={() => void pick(i)}
            data-testid={`quiz-${item.id}-opt-${i}`}
          >
            <InlineText text={o} />
          </button>
        ))}
      </div>
      {answered && (
        <div data-testid={`quiz-${item.id}-result`}>
          <p className={styles.status}>{right ? t('lesson.correct') : t('lesson.incorrect')}</p>
          {item.explanations[picked!] && <p className={styles.text} data-testid={`quiz-${item.id}-explanation`}><InlineText text={item.explanations[picked!]!} /></p>}
          {!right && (
            <button type="button" className={styles.secondary} onClick={() => setPicked(null)} data-testid={`quiz-${item.id}-retry`}>
              {t('lesson.retry')}
            </button>
          )}
        </div>
      )}
      {!answered && answeredRight && <p className={styles.status} data-testid={`quiz-${item.id}-done`}>{t('lesson.alreadyRight')}</p>}
      {onDiscuss && <button type="button" className={styles.secondary} disabled={busy} onClick={onDiscuss} data-testid={`discuss-${item.id}`}>{t('chat.discuss')}</button>}
      {error && <p role="alert" className={styles.error} data-testid={`error-${item.id}`}>{error}</p>}
    </article>
  );
}
